import { createContext, createSignal, useContext, onCleanup } from 'solid-js';
import { createLogger } from 'utils';

// Create a context for the event bus
export const EventBusContext = createContext();

// EID-64: debug-gated so production builds don't log/retain every event object.
// Enable with `localStorage.debug = 'eidos:event-bus'` (or `eidos:*`).
const log = createLogger('event-bus');

// EID-109: standalone mode (opened directly, not embedded in an iframe) has
// window.parent === window, so a postMessage there is a self-post that only
// re-enters this same window's own 'message' listener for no benefit. Split
// out so it can be unit tested without rendering the Solid component.
export const shouldPostToParent = () => window.parent !== window;

// Provider component to wrap the application with the event bus context
export const EventBusProvider = (props) => {
  const [lastEvent, setLastEvent] = createSignal(null);

  // Function to trigger an event and propagate it to the parent window
  const triggerEvent = (event) => {
    log('Event triggered', event);
    setLastEvent(event);
    // SECURITY: the wildcard target origin ('*') here is INTENTIONAL, not a
    // deferred fix. The renderer is designed to be embedded by any origin, and
    // these outbound events carry only non-sensitive visualisation interaction
    // data (clicks/selections/parameter values on the displayed spec) — there
    // is no token or credential in the payload. The inbound direction
    // (parent→iframe) is the one that matters for trust, and EID-67 already
    // origin-locked the token-bearing messages there.
    //
    // Caveat: if a future change ever attaches sensitive data to an outbound
    // event, do NOT keep '*'. Switch to a handshake-derived target origin —
    // capture the embedder's event.origin from its init message and post back
    // to exactly that — rather than introducing a build-time PARENT_ORIGIN or
    // an allow-list.
    if (shouldPostToParent()) {
      window.parent.postMessage(event, '*');
    }
  };

  const value = { triggerEvent, lastEvent };

  return (
    <EventBusContext.Provider value={value}>
      {props.children}
    </EventBusContext.Provider>
  );
};

// Hook to consume the event bus within components
export const useEventBus = () => {
  const eventBus = useContext(EventBusContext);
  if (!eventBus) {
    throw new Error('useEventBus must be used within an EventBusProvider');
  }
  return eventBus;
};
